/**
 * CP-14.4 — Admin / config controls for directory.title_taxonomy.
 *
 * Operator-only resource-type vocabulary management. Drives the Workforce
 * Offering picker (CP-14.2b) and the Crew Shape Search "Resource type"
 * select (CP-14.3).
 *
 * Invariants honored:
 *   - `code` is immutable once created. The UI shows it read-only on edit
 *     and only allows entry inside the create form.
 *   - No DELETE control. Operator deactivates via the Active switch; existing
 *     workforce_offering / contractor_self_id references stay intact.
 *   - In-use indicator (live offering count + self-id count) so the operator
 *     knows the blast radius of a deactivation.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

const AXES = ["av_integration", "live_events", "cross_axis"];
const AXIS_LABEL = {
  av_integration: "AV integration",
  live_events: "Live events",
  cross_axis: "Cross-axis",
};

const CODE_PATTERN = /^[a-z0-9_]{2,64}$/;

export function AdminTitleTaxonomyTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyCode, setBusyCode] = useState(null);
  const [dirty, setDirty] = useState({});

  const [newRow, setNewRow] = useState({
    code: "",
    display_label_default: "",
    surface_axis: "cross_axis",
    display_order: 0,
  });
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api("/admin/title-taxonomy");
      setItems(r.items || []);
      setDirty({});
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const updateLocal = (code, patch) => {
    // CP-14.4 safety prompt — confirm before deactivating an in-use title.
    if (patch.active === false) {
      const row = items.find((r) => r.code === code);
      if (row && row.in_use) {
        const msg =
          `This title is currently used by ${row.offering_count} workforce ` +
          `offering(s) and ${row.self_id_count} self-identification(s). ` +
          `Deactivation preserves existing references but removes the title ` +
          `from new selections. Continue?`;
        if (!window.confirm(msg)) {
          return; // user cancelled — keep row state unchanged
        }
      }
    }
    setItems((p) => p.map((r) => (r.code === code ? { ...r, ...patch } : r)));
    setDirty((p) => ({ ...p, [code]: { ...(p[code] || {}), ...patch } }));
  };

  const saveRow = async (code) => {
    const patch = dirty[code];
    if (!patch || Object.keys(patch).length === 0) return;
    setBusyCode(code);
    try {
      await api(`/admin/title-taxonomy/${code}`, { method: "PATCH", body: patch });
      toast.success(`Saved ${code}`);
      setDirty((p) => {
        const n = { ...p };
        delete n[code];
        return n;
      });
      await reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyCode(null);
    }
  };

  const createTitle = async () => {
    if (!CODE_PATTERN.test(newRow.code)) {
      toast.error("Code must be 2–64 chars, lowercase letters / digits / underscores.");
      return;
    }
    if (!newRow.display_label_default.trim()) {
      toast.error("Display label is required.");
      return;
    }
    setCreating(true);
    try {
      await api("/admin/title-taxonomy", {
        method: "POST",
        body: {
          code: newRow.code,
          display_label_default: newRow.display_label_default,
          surface_axis: newRow.surface_axis,
          display_order: Number(newRow.display_order) || 0,
          active: true,
        },
      });
      toast.success(`Created title "${newRow.code}"`);
      setNewRow({
        code: "",
        display_label_default: "",
        surface_axis: "cross_axis",
        display_order: 0,
      });
      await reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid={TID.adminTitlesTab}>
      <div className="mb-4 text-sm text-[var(--avcp-ink-soft)]">
        Resource-type vocabulary used by Workforce Offering rows and Crew
        Shape Search. The <code>code</code> is immutable once created — edit
        the display label or deactivate instead. Existing references to a
        deactivated title stay intact; the title simply disappears from new
        pickers.
      </div>

      {/* Create */}
      <section className="avcp-card p-5 mb-6">
        <h2 className="text-lg font-semibold mb-3">Add a resource type</h2>
        <div className="grid md:grid-cols-5 gap-3 items-end">
          <label className="block md:col-span-1">
            <span className="text-xs font-medium block mb-1">
              Code (immutable)
            </span>
            <input
              className="avcp-input"
              placeholder="e.g. level_5_technician"
              data-testid={TID.adminTitlesNewCode}
              value={newRow.code}
              onChange={(e) =>
                setNewRow({ ...newRow, code: e.target.value.toLowerCase() })
              }
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-medium block mb-1">Display label</span>
            <input
              className="avcp-input"
              placeholder="e.g. Level 5 Technician"
              data-testid={TID.adminTitlesNewLabel}
              value={newRow.display_label_default}
              onChange={(e) =>
                setNewRow({ ...newRow, display_label_default: e.target.value })
              }
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium block mb-1">Surface axis</span>
            <select
              className="avcp-input"
              data-testid={TID.adminTitlesNewAxis}
              value={newRow.surface_axis}
              onChange={(e) =>
                setNewRow({ ...newRow, surface_axis: e.target.value })
              }
            >
              {AXES.map((a) => (
                <option key={a} value={a}>
                  {AXIS_LABEL[a]}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium block mb-1">Order</span>
            <input
              type="number"
              min="0"
              className="avcp-input"
              data-testid={TID.adminTitlesNewOrder}
              value={newRow.display_order}
              onChange={(e) =>
                setNewRow({ ...newRow, display_order: e.target.value })
              }
            />
          </label>
        </div>
        <button
          className="avcp-btn primary mt-3"
          data-testid={TID.adminTitlesCreate}
          disabled={creating}
          onClick={createTitle}
        >
          {creating ? "Creating…" : "Create resource type"}
        </button>
      </section>

      {/* Edit grid */}
      <section className="avcp-card p-0 overflow-hidden">
        <div className="p-5 border-b border-[var(--avcp-line)] flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Existing resource types</h2>
          <span className="text-xs text-[var(--avcp-ink-soft)]">
            {loading ? "Loading…" : `${items.length} entries (incl. inactive)`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid={TID.adminTitlesTable}>
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-[var(--avcp-line)]">
                <th className="py-2 px-4">Code</th>
                <th className="py-2 px-4">Display label</th>
                <th className="py-2 px-4">Surface axis</th>
                <th className="py-2 px-4 w-20">Order</th>
                <th className="py-2 px-4 w-20">Active</th>
                <th className="py-2 px-4">In use</th>
                <th className="py-2 px-4 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.code}
                    className={`border-b border-[var(--avcp-line)] ${
                      r.active ? "" : "bg-slate-50/50 text-[var(--avcp-ink-soft)]"
                    }`}
                    data-testid={`${TID.adminTitlesRow}-${r.code}`}>
                  <td className="py-2 px-4 font-mono text-xs"
                      data-testid={`${TID.adminTitlesCode}-${r.code}`}>
                    {r.code}
                  </td>
                  <td className="py-2 px-4">
                    <input
                      className="avcp-input"
                      data-testid={`${TID.adminTitlesLabel}-${r.code}`}
                      value={r.display_label_default}
                      onChange={(e) =>
                        updateLocal(r.code, { display_label_default: e.target.value })
                      }
                    />
                  </td>
                  <td className="py-2 px-4">
                    <select
                      className="avcp-input"
                      data-testid={`${TID.adminTitlesAxis}-${r.code}`}
                      value={r.surface_axis}
                      onChange={(e) =>
                        updateLocal(r.code, { surface_axis: e.target.value })
                      }
                    >
                      {AXES.map((a) => (
                        <option key={a} value={a}>
                          {AXIS_LABEL[a]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="number"
                      min="0"
                      className="avcp-input"
                      data-testid={`${TID.adminTitlesOrder}-${r.code}`}
                      value={r.display_order}
                      onChange={(e) =>
                        updateLocal(r.code, { display_order: Number(e.target.value) || 0 })
                      }
                    />
                  </td>
                  <td className="py-2 px-4">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        data-testid={`${TID.adminTitlesActive}-${r.code}`}
                        checked={!!r.active}
                        onChange={(e) =>
                          updateLocal(r.code, { active: e.target.checked })
                        }
                      />
                    </label>
                  </td>
                  <td className="py-2 px-4 text-xs"
                      data-testid={`${TID.adminTitlesInUse}-${r.code}`}>
                    {r.in_use ? (
                      <span className="text-amber-700">
                        {r.offering_count} offering{r.offering_count === 1 ? "" : "s"},
                        {" "}{r.self_id_count} self-id{r.self_id_count === 1 ? "" : "s"}
                      </span>
                    ) : (
                      <span className="text-[var(--avcp-ink-soft)]">— not referenced</span>
                    )}
                  </td>
                  <td className="py-2 px-4">
                    <button
                      type="button"
                      className="avcp-btn ghost"
                      data-testid={`${TID.adminTitlesSaveRow}-${r.code}`}
                      disabled={!dirty[r.code] || busyCode === r.code}
                      onClick={() => saveRow(r.code)}
                    >
                      {busyCode === r.code ? "Saving…" : "Save"}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 px-4 text-center text-[var(--avcp-ink-soft)]">
                    No resource types defined. Add one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default AdminTitleTaxonomyTab;
